import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/ui/password-input';
import PasswordStrengthMeter from '../components/ui/password-strength-meter';
import { Label } from '../components/ui/label';
import { Flower } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: password },
        { withCredentials: true },
      );
      // Refresh AuthContext with the now-authenticated session (cookies set
      // by the backend on this same response).
      await checkAuth();
      navigate(data?.is_paid ? '/dashboard' : '/paywall');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1735052712464-9d24b69be5f5?w=1600&q=80&auto=format&fit=crop"
          alt="South Asian bride and groom walking together"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/20" />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <Flower className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
            <span className="font-brand text-2xl text-charcoal">Shaadi Planner</span>
          </div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight mb-4">
            Set a new password
          </h1>
          <p className="text-muted font-light mb-10">
            Choose a password you'll remember. You'll be signed in automatically.
          </p>

          {missingToken ? (
            <div
              className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-sm text-terracotta text-sm"
              data-testid="reset-password-missing-token"
            >
              This reset link is missing its security token. Please{' '}
              <Link to="/forgot-password" className="underline font-medium">request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div
                  className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-sm text-terracotta text-sm"
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-charcoal font-sans text-sm uppercase tracking-wider">
                  New password
                </Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field h-12"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
                <PasswordStrengthMeter password={password} data-testid="reset-password-strength" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-charcoal font-sans text-sm uppercase tracking-wider">
                  Confirm password
                </Label>
                <PasswordInput
                  id="confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-field h-12"
                  placeholder="Repeat your new password"
                  required
                  minLength={8}
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full h-12"
                data-testid="reset-password-submit-btn"
              >
                {loading ? 'Updating...' : 'Update password & sign in'}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-muted hover:text-terracotta transition-colors"
                  data-testid="reset-back-to-login-link"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
